/**
 * Sunset: stop sending marketing to people who never engage (promised to
 * AWS with SES production access). A daily sweep puts them on the
 * never-email list as "Never engaged"; a click, or signing up again with
 * consent, takes them off. Unsubscribes, complaints, bounces and failed
 * verifications never come off this way.
 *
 * Changed by a super admin; read by anyone who edits campaigns.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, NumberInput, SegmentedControl, Select, Stack, Switch, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useAuth } from '../../auth/AuthProvider';
import { useEmailBrands, useSaveSunsetSettings, useSunsetSettings, type SunsetSettings } from '../../api/queries';

const BLANK: SunsetSettings = { enabled: true, min_sends: 3, max_days: 90, signal: 'opens_or_clicks', brands: [] };

export function SunsetCard() {
  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin';
  const settings = useSunsetSettings(true);
  const save = useSaveSunsetSettings();
  const brands = useEmailBrands();
  const [form, setForm] = useState<SunsetSettings>(BLANK);
  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);
  const set = <K extends keyof SunsetSettings>(k: K, v: SunsetSettings[K]) => setForm((f) => ({ ...f, [k]: v }));
  const brandName = (id: string) => brands.data?.brands.find((b) => b.id === id)?.name ?? 'Deleted brand';
  const unused = (brands.data?.brands ?? []).filter((b) => !(form.brands ?? []).some((o) => o.brand_id === b.id));

  return (
    <Card withBorder radius="md" padding="md" data-testid="sunset-card">
      <Stack gap="sm">
        <Group gap="xs">
          <Text size="sm" fw={600}>Sunset people who never engage</Text>
          <Badge variant="light" color={form.enabled ? 'teal' : 'gray'}>{form.enabled ? 'On' : 'Off'}</Badge>
        </Group>
        <Text size="xs" c="dimmed" maw={680}>
          Sending to people who never open or click hurts how inboxes treat every email you send. Once a
          day, anyone who has been sent enough without engaging stops getting marketing. If they later
          click a link, or sign up again and say yes to email, they come back automatically.
        </Text>
        <Switch label="Sunset never-engaged addresses" checked={form.enabled} disabled={!canEdit}
          onChange={(e) => { const on = e.currentTarget.checked; set('enabled', on); }} />
        <Group grow align="flex-start">
          <NumberInput label="After this many emails" min={1} max={1000} value={form.min_sends} disabled={!canEdit}
            onChange={(v) => set('min_sends', Number(v) || 3)} />
          <NumberInput label="Or after being emailed for" suffix=" days" min={1} max={3650} value={form.max_days} disabled={!canEdit}
            onChange={(v) => set('max_days', Number(v) || 90)} />
        </Group>
        <Stack gap={4}>
          <Text size="sm" fw={500}>What counts as engaging</Text>
          <SegmentedControl size="xs" value={form.signal ?? 'opens_or_clicks'} disabled={!canEdit}
            onChange={(v) => set('signal', v)}
            data={[
              { value: 'clicks', label: 'Clicks only (recommended)' },
              { value: 'opens_or_clicks', label: 'Opens or clicks' },
            ]} />
          <Text size="xs" c="dimmed">
            Apple Mail opens every email itself to protect privacy, so an open often means nothing. Clicks
            are the honest signal.
          </Text>
        </Stack>
        <Stack gap={6}>
          <Text size="sm" fw={500}>Brand rules</Text>
          <Text size="xs" c="dimmed">
            Optional. A brand can count its own emails with stricter numbers, or not count towards a sunset at all.
          </Text>
          {(form.brands ?? []).map((b, i) => (
            <Group key={b.brand_id} gap="xs" wrap="nowrap" align="flex-end">
              <Text size="sm" w={140} truncate>{brandName(b.brand_id)}</Text>
              <Switch size="xs" label="Counts" checked={b.enabled ?? true} disabled={!canEdit}
                onChange={(e) => { const on = e.currentTarget.checked; set('brands', (form.brands ?? []).map((x, j) => (j === i ? { ...x, enabled: on } : x))); }} />
              <NumberInput size="xs" w={110} label="Emails" min={1} value={b.min_sends ?? 3} disabled={!canEdit || b.enabled === false}
                onChange={(v) => set('brands', (form.brands ?? []).map((x, j) => (j === i ? { ...x, min_sends: Number(v) || 3 } : x)))} />
              <NumberInput size="xs" w={110} label="Days" min={1} value={b.max_days ?? 90} disabled={!canEdit || b.enabled === false}
                onChange={(v) => set('brands', (form.brands ?? []).map((x, j) => (j === i ? { ...x, max_days: Number(v) || 90 } : x)))} />
              {canEdit && (
                <ActionIcon variant="subtle" color="gray" aria-label={`Remove the ${brandName(b.brand_id)} rule`}
                  onClick={() => set('brands', (form.brands ?? []).filter((_, j) => j !== i))}>
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>
          ))}
          {canEdit && unused.length > 0 && (
            <Select size="xs" maw={240} placeholder="Add a rule for a brand" leftSection={<IconPlus size={12} />}
              data={unused.map((b) => ({ value: b.id, label: b.name }))} value={null}
              onChange={(id) => id && set('brands', [...(form.brands ?? []), { brand_id: id, enabled: true, min_sends: form.min_sends ?? 3, max_days: form.max_days ?? 90 }])} />
          )}
        </Stack>
        {!canEdit && (
          <Alert variant="light" color="gray"><Text size="xs">Only a super admin can change these rules.</Text></Alert>
        )}
        {canEdit && (
          <Group justify="flex-end">
            <Button size="xs" loading={save.isPending} onClick={() => save.mutate(form, {
              onSuccess: () => notifications.show({ color: 'teal', title: 'Saved', message: 'Sunset rules updated. The next sweep uses them.' }),
              onError: (e) => notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
            })}>Save rules</Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
